import chains from '@safe-global/utils/config/chains'
import { getSafeL2SingletonDeployments, getSafeSingletonDeployments } from '@safe-global/safe-deployments'
import ExternalStore from '@safe-global/utils/services/ExternalStore'
import { Gnosis_safe__factory } from '@safe-global/utils/types/contracts'
import Safe, { type ContractNetworksConfig } from '@safe-global/protocol-kit'
import { isValidMasterCopy } from '@safe-global/utils/services/contracts/safeContracts'
import { isPredictedSafeProps, isReplayedSafeProps } from '@/features/counterfactual/utils'
import { isLegacyVersion } from '@safe-global/utils/services/contracts/utils'
import { isInDeployments } from '@safe-global/utils/hooks/coreSDK/utils'
import type { SafeCoreSDKProps } from '@safe-global/utils/hooks/coreSDK/types'
import { keccak256 } from 'ethers'
import {
  getL2MasterCopyVersionByCodeHash,
  isL2MasterCopyCodeHash,
} from '@safe-global/utils/services/contracts/deployments'
import { logError, Errors } from '@/services/exceptions'

// Safe Core SDK
export const initSafeSDK = async ({
  provider,
  chainId,
  address,
  version,
  implementationVersionState,
  implementation,
  undeployedSafe,
  contractAddresses,
}: SafeCoreSDKProps): Promise<Safe | undefined> => {
  const providerNetwork = (await provider.getNetwork()).chainId
  if (providerNetwork !== BigInt(chainId)) {
    return
  }

  let safeVersion = version ?? (await Gnosis_safe__factory.connect(address, provider).VERSION())
  let isL1SafeSingleton = chainId === chains.eth
  let contractNetworks: ContractNetworksConfig | undefined

  // If it is an official deployment we should still initiate the safeSDK
  if (!isValidMasterCopy(implementationVersionState)) {
    const masterCopy = implementation

    const safeL1Deployment = getSafeSingletonDeployments({ network: chainId, version: safeVersion })
    const safeL2Deployment = getSafeL2SingletonDeployments({ network: chainId, version: safeVersion })

    isL1SafeSingleton = isInDeployments(masterCopy, safeL1Deployment?.networkAddresses[chainId])
    const isL2SafeMasterCopy = isInDeployments(masterCopy, safeL2Deployment?.networkAddresses[chainId])

    if (!isL1SafeSingleton && !isL2SafeMasterCopy) {
      try {
        const code = await provider.getCode(masterCopy)

        if (!code || code === '0x') {
          console.warn(`[SafeSDK] No bytecode found for mastercopy at ${masterCopy}`)
          return
        }

        const codeHash = keccak256(code)
        const isUpgradeableL2MasterCopy = isL2MasterCopyCodeHash(codeHash)

        if (!isUpgradeableL2MasterCopy) {
          console.warn(`[SafeSDK] Mastercopy at ${masterCopy} is not a recognized L2 mastercopy`)
          return
        }

        const upgradeableVersion = getL2MasterCopyVersionByCodeHash(codeHash)

        if (!upgradeableVersion) {
          console.warn(`[SafeSDK] Could not determine version for L2 mastercopy at ${masterCopy}`)
          return
        }

        // Use the custom mastercopy address with the SDK and include all contract addresses if provided
        contractNetworks = {
          [chainId]: {
            safeSingletonAddress: masterCopy,
            ...(contractAddresses?.safeProxyFactoryAddress && {
              safeProxyFactoryAddress: contractAddresses.safeProxyFactoryAddress,
            }),
            ...(contractAddresses?.multiSendAddress && { multiSendAddress: contractAddresses.multiSendAddress }),
            ...(contractAddresses?.multiSendCallOnlyAddress && {
              multiSendCallOnlyAddress: contractAddresses.multiSendCallOnlyAddress,
            }),
            ...(contractAddresses?.fallbackHandlerAddress && {
              fallbackHandlerAddress: contractAddresses.fallbackHandlerAddress,
            }),
            ...(contractAddresses?.signMessageLibAddress && {
              signMessageLibAddress: contractAddresses.signMessageLibAddress,
            }),
            ...(contractAddresses?.createCallAddress && { createCallAddress: contractAddresses.createCallAddress }),
            ...(contractAddresses?.simulateTxAccessorAddress && {
              simulateTxAccessorAddress: contractAddresses.simulateTxAccessorAddress,
            }),
          },
        }

        safeVersion = upgradeableVersion
        isL1SafeSingleton = false
      } catch (error) {
        logError(Errors._808, error)
        return
      }
    }

    if (isL2SafeMasterCopy) {
      isL1SafeSingleton = false
    }
  }
  // Legacy Safe contracts
  if (isLegacyVersion(safeVersion)) {
    isL1SafeSingleton = true
  }

  // For custom chains, configure all contract addresses
  // When explicit contract addresses are provided via chain config, always use them
  // to ensure all contracts (multiSend, multiSendCallOnly, fallbackHandler, etc.) are properly configured
  if (contractAddresses && !contractNetworks) {
    const networkConfig: any = {}
    
    if (contractAddresses.safeSingletonAddress) {
      networkConfig.safeSingletonAddress = contractAddresses.safeSingletonAddress
    }
    if (contractAddresses.safeProxyFactoryAddress) {
      networkConfig.safeProxyFactoryAddress = contractAddresses.safeProxyFactoryAddress
    }
    if (contractAddresses.multiSendAddress) {
      networkConfig.multiSendAddress = contractAddresses.multiSendAddress
    }
    if (contractAddresses.multiSendCallOnlyAddress) {
      networkConfig.multiSendCallOnlyAddress = contractAddresses.multiSendCallOnlyAddress
    }
    if (contractAddresses.fallbackHandlerAddress) {
      networkConfig.fallbackHandlerAddress = contractAddresses.fallbackHandlerAddress
    }
    if (contractAddresses.signMessageLibAddress) {
      networkConfig.signMessageLibAddress = contractAddresses.signMessageLibAddress
    }
    if (contractAddresses.createCallAddress) {
      networkConfig.createCallAddress = contractAddresses.createCallAddress
    }
    if (contractAddresses.simulateTxAccessorAddress) {
      networkConfig.simulateTxAccessorAddress = contractAddresses.simulateTxAccessorAddress
    }

    contractNetworks = {
      [chainId]: networkConfig,
    }
  }

  if (undeployedSafe) {
    if (isPredictedSafeProps(undeployedSafe.props) || isReplayedSafeProps(undeployedSafe.props)) {
      return Safe.init({
        provider: provider._getConnection().url,
        isL1SafeSingleton,
        ...(contractNetworks ? { contractNetworks } : {}),
        predictedSafe: undeployedSafe.props,
      })
    }
    // We cannot initialize a Core SDK for replayed Safes yet.
    return
  }

  return Safe.init({
    provider: provider._getConnection().url,
    safeAddress: address,
    isL1SafeSingleton,
    ...(contractNetworks ? { contractNetworks } : {}),
  })
}

export const {
  getStore: getSafeSDK,
  setStore: setSafeSDK,
  useStore: useSafeSDK,
} = new ExternalStore<Safe | undefined>()
